/**
 * Abrir novo ticket pelo portal: mesa (GET /portal/desks, só portal_enabled)
 * + título + descrição. Usuário vinculado a N clientes: a API responde 422
 * exigindo clientId — aí o form revela o campo de cliente (com opções quando
 * o 422 as traz em `details`; senão, código numérico) e reenviamos.
 * Sucesso → navega para o detalhe do ticket criado.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import type { ApiError } from '@/lib/api-client';
import { applyOpeningTemplate } from '@/lib/opening-template';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { errorMessage, portalDesksQuery, useCreatePortalTicket } from './portal-queries';
import { PortalLink, portalPath, usePortalNavigate } from './portal-nav';
import { useIsImpersonating } from '@/auth/impersonation';
import { ReadOnlyNotice } from '@/features/impersonation/read-only-notice';

interface ClientOption {
  id: number;
  name: string;
}

/**
 * Tenta extrair opções de cliente do `details` do 422 (formatos tolerados:
 * array de {id, name} direto ou aninhado em details.clients).
 */
function extractClientOptions(details: unknown): ClientOption[] {
  const list = Array.isArray(details)
    ? details
    : details && typeof details === 'object' && Array.isArray((details as { clients?: unknown }).clients)
      ? (details as { clients: unknown[] }).clients
      : [];
  return list.filter(
    (c): c is ClientOption =>
      c !== null &&
      typeof c === 'object' &&
      typeof (c as { id?: unknown }).id === 'number' &&
      typeof (c as { name?: unknown }).name === 'string'
  );
}

/** true quando o 422 é o pedido de clientId (usuário com N clientes). */
function isClientRequiredError(e: ApiError): boolean {
  return e.status === 422 && e.error.toLowerCase().includes('client');
}

const ERROR_LABELS: Record<string, string> = {
  desk_not_portal: 'Esta mesa não está disponível no portal.',
  desk_not_found: 'Mesa não encontrada.',
  ticket_not_open: 'O ticket não está mais aberto.',
};

function submitErrorLabel(e: unknown): string {
  const raw = errorMessage(e);
  return ERROR_LABELS[raw] ?? (raw.startsWith('http_') ? 'Erro inesperado. Tente novamente.' : raw);
}

/**
 * Na aba de depuração (spec 020) o formulário não aparece: nada é criado em
 * nome do cliente, e a API recusaria de qualquer forma (403). A tela diz o que
 * ele veria aqui.
 */
export function PortalNewTicket() {
  const impersonating = useIsImpersonating();
  if (!impersonating) return <PortalNewTicketForm />;
  return (
    <div className="space-y-4">
      <PortalLink
        to={portalPath.tickets}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Voltar para meus tickets
      </PortalLink>
      <ReadOnlyNotice>
        Sessão de depuração: aqui o cliente abriria um ticket, escolhendo a mesa, o título e a
        descrição. Abrir fica desligado — nada é criado em nome dele.
      </ReadOnlyNotice>
    </div>
  );
}

function PortalNewTicketForm() {
  const desks = useQuery(portalDesksQuery());
  const create = useCreatePortalTicket();
  const portalNavigate = usePortalNavigate();

  const [deskId, setDeskId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [needsClient, setNeedsClient] = useState(false);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientId, setClientId] = useState('');
  const [error, setError] = useState<string | null>(null);

  /**
   * Troca de mesa: aplica o openingTemplate da mesa escolhida quando a
   * descrição está intacta (regra única em lib/opening-template). Texto
   * digitado pelo usuário NUNCA é sobrescrito.
   */
  const selectDesk = (nextId: string) => {
    const list = desks.data ?? [];
    const next = applyOpeningTemplate(
      description,
      list.find((d) => String(d.id) === deskId)?.openingTemplate ?? null,
      list.find((d) => String(d.id) === nextId)?.openingTemplate ?? null
    );
    if (next !== null) setDescription(next);
    setDeskId(nextId);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!deskId) {
      setError('Selecione a mesa de atendimento.');
      return;
    }
    if (!title.trim() || !description.trim()) {
      setError('Preencha o título e a descrição.');
      return;
    }
    if (needsClient && !clientId) {
      setError('Selecione para qual cliente é a solicitação.');
      return;
    }
    create.mutate(
      {
        deskId: Number(deskId),
        title: title.trim(),
        description: description.trim(),
        clientId: needsClient && clientId ? Number(clientId) : undefined,
      },
      {
        onSuccess: (ticket) => portalNavigate(portalPath.ticket(ticket.ticketNumber)),
        onError: (err) => {
          const apiErr = err as unknown as ApiError;
          if (isClientRequiredError(apiErr)) {
            setNeedsClient(true);
            const options = extractClientOptions(apiErr.details);
            if (options.length > 0) setClientOptions(options);
            setError(
              'Você está vinculado a mais de um cliente — informe para qual cliente é esta solicitação.'
            );
            return;
          }
          setError(submitErrorLabel(err));
        },
      }
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PortalLink
        to={portalPath.tickets}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Voltar para meus tickets
      </PortalLink>

      <div className="rounded-lg border bg-card p-5 shadow-xs">
        <h1 className="text-lg font-semibold text-foreground">Abrir novo ticket</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Descreva sua solicitação e nossa equipe dará andamento.
        </p>

        {desks.isPending ? (
          <div aria-busy className="mt-4 space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : desks.isError ? (
          <EmptyState message={`Erro ao carregar as mesas: ${errorMessage(desks.error)}`} />
        ) : desks.data.length === 0 ? (
          <EmptyState message="Nenhuma mesa disponível para abertura pelo portal." />
        ) : (
          <form className="mt-4 flex flex-col gap-4" onSubmit={submit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pnt-desk" className="text-[13px] font-semibold">
                Mesa de atendimento<span className="-ml-0.5 text-destructive">*</span>
              </Label>
              <Select
                id="pnt-desk"
                value={deskId}
                onChange={(e) => selectDesk(e.target.value)}
                required
              >
                <option value="" disabled>
                  Selecione a mesa…
                </option>
                {desks.data.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.displayName}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pnt-title" className="text-[13px] font-semibold">
                Título<span className="-ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="pnt-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Resumo da solicitação"
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pnt-description" className="text-[13px] font-semibold">
                Descrição<span className="-ml-0.5 text-destructive">*</span>
              </Label>
              <Textarea
                id="pnt-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Detalhe o que você precisa…"
                className="min-h-28"
                required
              />
            </div>

            {needsClient ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pnt-client" className="text-[13px] font-semibold">
                  Cliente<span className="-ml-0.5 text-destructive">*</span>
                </Label>
                {clientOptions.length > 0 ? (
                  <Select
                    id="pnt-client"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    required
                  >
                    <option value="" disabled>
                      Selecione o cliente…
                    </option>
                    {clientOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    id="pnt-client"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value.replace(/\D/g, ''))}
                    inputMode="numeric"
                    placeholder="Código do cliente"
                    required
                  />
                )}
              </div>
            ) : null}

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? (
                  <Spinner className="size-4 border-primary-foreground" />
                ) : (
                  <Plus className="size-4" strokeWidth={2.5} aria-hidden />
                )}
                Abrir ticket
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
